import type { VehicleOnDealV2 } from "@credit-acceptance/step6-bff-client";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "../../../../../components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import { LabeledValue } from "../../../../../components/ui/labeledValue";

export const NADA_DETAILS_PANEL_ID = "nada-details-panel";

const EMPTY_VALUE = "NA";

interface NadaDetailsModalProperties {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** The deal's vehicle; the popup reads its NADA-backed identity fields. */
  readonly vehicleOnDeal?: VehicleOnDealV2;
}

/**
 * NADA vehicle detail modal (spec §5.1's popup P2), opened from the VIN label link. View-only.
 *
 * Spec §5.1 describes P2 as the vehicle's NADA detail: year, make, series/model and body/trim. The
 * BFF does not expose the NADA record (`vehicleStruc`) yet, so only the three identity fields the
 * dashboard already carries are shown; see the @TODO below for the rest.
 *
 * Openable in read-only mode, same as the Odometer popup — spec §3.4's Decision.
 */
export function NadaDetailsModal({ open, onOpenChange, vehicleOnDeal }: NadaDetailsModalProperties) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent id={NADA_DETAILS_PANEL_ID} showCloseButton={false} className="gap-0 overflow-hidden p-0">
        <div className="bg-primary text-primary-foreground flex items-center justify-between border-b px-4 py-2">
          <DialogTitle className="text-primary-foreground text-sm font-semibold">
            {t("vehicle.nadaDetails.title")}
          </DialogTitle>
          <DialogDescription className="sr-only">{t("vehicle.nadaDetails.description")}</DialogDescription>
          <DialogClose
            className="text-primary-foreground/80 hover:text-primary-foreground p-0.5 transition-colors"
            aria-label={t("vehicle.nadaDetails.close")}
          >
            <X className="size-4.5" aria-hidden="true" />
          </DialogClose>
        </div>

        {/* @TODO: Add series and body/trim (spec §5.1) once the BFF exposes the NADA detail record. */}
        <div className="grid grid-cols-3 gap-4 p-4">
          <LabeledValue label={t("vehicle.contractVehicle.year")} value={vehicleOnDeal?.year || EMPTY_VALUE} />
          <LabeledValue label={t("vehicle.contractVehicle.make")} value={vehicleOnDeal?.make || EMPTY_VALUE} />
          <LabeledValue label={t("vehicle.contractVehicle.model")} value={vehicleOnDeal?.model || EMPTY_VALUE} />
        </div>

        <DialogFooter className="justify-center border-t px-4 py-3 sm:justify-center">
          <DialogClose asChild>
            <Button type="button" size="xs">
              {t("vehicle.nadaDetails.close")}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
